import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default function BookYourStay() {
  const ref = useRef<HTMLElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<"idle" | "success">("idle");

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Bottom-up full-screen takeover reveal
      if (innerRef.current && ref.current) {
        gsap.fromTo(
          innerRef.current,
          { yPercent: 100 },
          {
            yPercent: 0,
            ease: "none",
            scrollTrigger: {
              trigger: ref.current,
              start: "top bottom",
              end: "top top",
              scrub: true,
            },
          }
        );
      }

      gsap.utils.toArray<HTMLElement>(".book-reveal").forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 24 },
          {
            opacity: 1,
            y: 0,
            duration: 0.9,
            ease: "power3.out",
            scrollTrigger: { trigger: el, start: "top 92%" },
          }
        );
      });
    }, ref);
    return () => ctx.revert();
  }, []);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("success");
    setTimeout(() => setStatus("idle"), 4500);
    (e.currentTarget as HTMLFormElement).reset();
  };

  return (
    <section
      id="book"
      ref={ref}
      className="relative"
      style={{ height: "180vh" }}
    >
      <div className="sticky top-0 h-screen overflow-hidden">
        <div
          ref={innerRef}
          className="absolute inset-0 text-background-50 will-change-transform"
        >
          {/* Dusk mountain scene with warm interior lights */}
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Cinematic%20alpine%20retreat%20exterior%20at%20dusk%20blue%20hour%2C%20contemporary%20timber%20and%20stone%20lodge%20with%20warm%20amber%20interior%20lights%20glowing%20softly%20through%20tall%20windows%2C%20snow%20covered%20mountain%20landscape%20in%20the%20background%20under%20deep%20indigo%20twilight%20sky%2C%20soft%20drifting%20mist%2C%20pine%20forest%2C%20editorial%20luxury%20hospitality%20photography%2C%20quiet%20luxury%2C%20mineral%20cool%20palette%20with%20warm%20interior%20window%20glow%2C%20fine%20art%20composition%20with%20negative%20space&width=2400&height=1800&seq=book-dusk-arrival-02&orientation=landscape"
              alt="Arrival at the retreat, dusk"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/55 to-black/80 pointer-events-none" />
          </div>

          <div className="relative h-full max-w-7xl mx-auto px-6 md:px-10 flex flex-col">
            <div className="pt-10 md:pt-14">
              <div className="flex items-center gap-4 book-reveal">
                <span className="w-8 h-px bg-background-50/50" />
                <span className="eyebrow text-background-50/80">07 — Book Your Stay</span>
              </div>

              <h2 className="book-reveal font-display font-light text-3xl md:text-[42px] leading-[1.08] tracking-tight mt-5 md:mt-6 max-w-3xl text-background-50">
                Arrive slowly.
                <br />
                Stay longer than intended.
              </h2>
            </div>

            <div className="grid md:grid-cols-12 gap-8 md:gap-12 mt-8 md:mt-10 flex-1 min-h-0">
              <div className="book-reveal md:col-span-5 space-y-5 md:space-y-6">
                <p className="text-background-50/85 text-[15px] font-light leading-relaxed max-w-md">
                  Reservations open twelve months in advance. Our concierge confirms within twenty-four hours, with private transfer available on request.
                </p>

                <div className="space-y-4 border-t border-background-50/20 pt-5">
                  <div>
                    <div className="eyebrow text-background-50/55 text-[10px]">Concierge</div>
                    <p className="font-display text-[15px] md:text-base mt-1 text-background-50 font-light">
                      reservations@alpineretreat.co
                    </p>
                  </div>
                  <div>
                    <div className="eyebrow text-background-50/55 text-[10px]">Direct</div>
                    <p className="font-display text-[15px] md:text-base mt-1 text-background-50 font-light">
                      +41 81 000 000
                    </p>
                  </div>
                  <div>
                    <div className="eyebrow text-background-50/55 text-[10px]">Address</div>
                    <p className="font-display text-[15px] md:text-base mt-1 leading-snug text-background-50 font-light">
                      Passhöhe 2148 — CH-7524 Engadin, Switzerland
                    </p>
                  </div>
                </div>
              </div>

              <form
                data-readdy-form
                onSubmit={onSubmit}
                className="book-reveal md:col-span-7 rounded-xl border border-background-50/15 p-5 md:p-7 bg-background-950/40 backdrop-blur-xl relative"
                style={{
                  boxShadow:
                    "0 20px 60px -20px rgba(0,0,0,0.55), inset 0 1px 0 0 rgba(255,255,255,0.06)",
                }}
              >
                <div className="hp-field" aria-hidden="true">
                  <label>
                    Company
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      readOnly
                    />
                  </label>
                </div>

                <div className="grid md:grid-cols-2 gap-x-6 gap-y-4">
                  <div>
                    <label className="eyebrow text-background-50/55 text-[10px] block mb-1.5">
                      Full name
                    </label>
                    <input
                      name="name"
                      required
                      className="w-full bg-transparent border-b border-background-50/20 focus:border-background-50/80 outline-none py-2 text-background-50 text-sm font-light transition-colors placeholder:text-background-50/40"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="eyebrow text-background-50/55 text-[10px] block mb-1.5">
                      Email
                    </label>
                    <input
                      name="email"
                      type="email"
                      required
                      className="w-full bg-transparent border-b border-background-50/20 focus:border-background-50/80 outline-none py-2 text-background-50 text-sm font-light transition-colors placeholder:text-background-50/40"
                      placeholder="you@domain.com"
                    />
                  </div>
                  <div>
                    <label className="eyebrow text-background-50/55 text-[10px] block mb-1.5">
                      Arrival
                    </label>
                    <input
                      name="arrival"
                      type="date"
                      required
                      className="w-full bg-transparent border-b border-background-50/20 focus:border-background-50/80 outline-none py-2 text-background-50 text-sm font-light transition-colors [color-scheme:dark]"
                    />
                  </div>
                  <div>
                    <label className="eyebrow text-background-50/55 text-[10px] block mb-1.5">
                      Departure
                    </label>
                    <input
                      name="departure"
                      type="date"
                      required
                      className="w-full bg-transparent border-b border-background-50/20 focus:border-background-50/80 outline-none py-2 text-background-50 text-sm font-light transition-colors [color-scheme:dark]"
                    />
                  </div>
                  <div>
                    <label className="eyebrow text-background-50/55 text-[10px] block mb-1.5">
                      Guests
                    </label>
                    <select
                      name="guests"
                      className="w-full bg-transparent border-b border-background-50/20 focus:border-background-50/80 outline-none py-2 text-background-50 text-sm font-light transition-colors appearance-none cursor-pointer"
                      defaultValue="2 guests"
                    >
                      <option className="bg-background-950">1 guest</option>
                      <option className="bg-background-950">2 guests</option>
                      <option className="bg-background-950">3 guests</option>
                      <option className="bg-background-950">4 guests</option>
                    </select>
                  </div>
                  <div>
                    <label className="eyebrow text-background-50/55 text-[10px] block mb-1.5">
                      Retreat
                    </label>
                    <select
                      name="retreat"
                      className="w-full bg-transparent border-b border-background-50/20 focus:border-background-50/80 outline-none py-2 text-background-50 text-sm font-light transition-colors appearance-none cursor-pointer"
                      defaultValue="Ridge Suite"
                    >
                      <option className="bg-background-950">Ridge Suite</option>
                      <option className="bg-background-950">Forest Cabin</option>
                      <option className="bg-background-950">Summit Residence</option>
                    </select>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="eyebrow text-background-50/55 text-[10px] block mb-1.5">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    rows={2}
                    maxLength={500}
                    className="w-full bg-transparent border-b border-background-50/20 focus:border-background-50/80 outline-none py-2 text-background-50 font-light text-sm transition-colors resize-none placeholder:text-background-50/40"
                    placeholder="Anything the concierge should know."
                  />
                </div>

                <div className="mt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <p className="text-background-50/50 text-[11px] max-w-xs leading-relaxed">
                    By submitting, you agree to be contacted by our reservations team. No marketing, no third parties.
                  </p>
                  <button
                    type="submit"
                    className="eyebrow whitespace-nowrap px-6 py-3 rounded-md bg-background-50 text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer text-[11px] tracking-[0.18em]"
                  >
                    Request Reservation
                  </button>
                </div>

                {status === "success" && (
                  <p className="mt-4 text-background-50 text-sm">
                    Received. Our concierge will be in touch within 24 hours.
                  </p>
                )}
              </form>
            </div>

            <footer className="mt-auto border-t border-background-50/15 py-4 md:py-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="w-6 h-6 flex items-center justify-center border border-background-50/50 rounded-full">
                  <i className="ri-landscape-line text-background-50 text-xs"></i>
                </span>
                <span className="font-display text-sm text-background-50">
                  Alpine Retreat
                </span>
              </div>
              <div className="flex flex-wrap gap-5 eyebrow text-background-50/55 text-[10px]">
                <a href="#" className="hover:text-background-50 cursor-pointer">Journal</a>
                <a href="#" className="hover:text-background-50 cursor-pointer">Press</a>
                <a href="#" className="hover:text-background-50 cursor-pointer">Careers</a>
                <a href="#" className="hover:text-background-50 cursor-pointer">Privacy</a>
              </div>
              <p className="text-background-50/45 text-[10px]">
                © MMXXV Alpine Retreat — Made in the Alps
              </p>
            </footer>
          </div>
        </div>
      </div>
    </section>
  );
}